// src/oura/screens/ClientRequests.tsx
//
// Client requests: every change a client asks for, from any channel, in one
// staff list. Staff paste the client's message or add a screenshot, Oura reads
// it and splits it into requests, and emails to oura@ land in the inbox card
// until staff pick their client. Staff only, no business in focus.
//
// Live on GET /change-requests and its sibling routes (see
// ../../services/aeo/changeRequests.ts). Production keeps the page off until
// its flag ships (REQUESTS_ON in ../requests/flag.ts).

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
    CalendarDaysIcon,
    CheckIcon,
    ClipboardPasteIcon,
    GlobeIcon,
    ImageIcon,
    InboxIcon,
    MailIcon,
    MessageSquareIcon,
    MessageSquarePlusIcon,
    SparklesIcon,
    UserRoundIcon,
    type LucideIcon,
} from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import { useUserProfileContext } from "@context/user_profile";
import { useWritesPaused } from "@context/maintenance";
import {
    Drawer,
    DrawerBlock,
    DrawerFoot,
    DrawerHead,
    DrawerNotice,
    DrawerPulse,
    FoldScope,
    GroupFold,
    IconSquare,
    ListRow,
    PageHead,
    PillButton,
    StatusKey,
    Surface,
    Tag,
    TYPE,
    type Tone,
} from "@oura/ui";
import {
    addChangeRequests,
    assignUnmatchedEmail,
    estimateChangeRequestRead,
    listChangeRequests,
    listRequestClients,
    listUnmatchedEmails,
    patchChangeRequest,
    readChangeRequests,
    replyToChangeRequest,
    uploadChangeRequestImage,
    CHANGE_REQUEST_STATUSES,
    type ChangeRequestItem,
    type ChangeRequestPatch,
    type ChangeRequestRow,
    type ChangeRequestSource,
    type ChangeRequestStatus,
    type ReadEngine,
    type ReadEngineOption,
    type ReadEstimate,
    type RequestClient,
    type UnmatchedEmail,
} from "@service/aeo/changeRequests";
import { everyMessage, requestMessages } from "@service/aeo/clientReview";
import { RadioList } from "../components/docs/create/RadioList";
import { costText } from "../docs/skillRun";
import { cn } from "../lib/cn";
import { ouraHref } from "../nav";
import { kindOf, textOf, type Attachment } from "../requests/files";
import { ClientMessage, EditButton, PasteBox } from "../requests/RequestBrief";
import { REPLY_PICTURES_ON, ReplyPictureGrid, ReplyPicturePicker, usePictureInput } from "../components/ReplyPictures";

/* ------------------------------------------------------------------------- *
 * Statuses and sources
 * ------------------------------------------------------------------------- */

const STATUS: Record<ChangeRequestStatus, { label: string; tone: Tone }> = {
    open: { label: "Needs us", tone: "blue" },
    in_progress: { label: "In progress", tone: "teal" },
    waiting_on_client: { label: "Waiting on client", tone: "amber" },
    answered: { label: "Answered", tone: "violet" },
    done: { label: "Done", tone: "green" },
};

const SOURCE: Record<ChangeRequestSource, { icon: LucideIcon; label: string; tone: Tone }> = {
    oura: { icon: MessageSquarePlusIcon, label: "Asked in Oura", tone: "blue" },
    text: { icon: MessageSquareIcon, label: "Pasted message", tone: "green" },
    screenshot: { icon: ImageIcon, label: "Screenshot", tone: "purple" },
    email: { icon: MailIcon, label: "Email to oura@", tone: "orange" },
};

const statusOf = (s: string) => STATUS[s as ChangeRequestStatus] ?? { label: s, tone: "gray" as Tone };
const sourceOf = (s: string) => SOURCE[s as ChangeRequestSource] ?? { icon: MessageSquareIcon, label: s, tone: "gray" as Tone };

const TODAY = new Date();
const DAY = 86_400_000;

function dayText(iso: string): string {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
function askedText(iso: string): string {
    const day = (d: Date) => Math.floor((d.getTime() + 8 * 3_600_000) / DAY); // Malaysian calendar day
    const days = day(TODAY) - day(new Date(iso));
    if (days <= 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
}
function isLate(r: ChangeRequestRow): boolean {
    return Boolean(r.due && r.status !== "done" && new Date(`${r.due}T23:59:00+08:00`) < TODAY);
}
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** A stored screenshot, as the list shows it. */
function storedAttachment(f: { fileKey?: string; name: string; url: string }): Attachment {
    const type = /\.pdf$/i.test(f.name) ? "application/pdf" : /\.(png|jpe?g|webp)$/i.test(f.name) ? "image/png" : "";
    return { id: f.fileKey ?? f.name, name: f.name, kind: kindOf(type, f.name), url: f.url, size: null, fileKey: f.fileKey };
}

/** What the page is showing, as loaded from the server. */
type Load =
    | { state: "loading"; query: string }
    | { state: "failed"; query: string; message: string }
    | { state: "ready"; query: string; rows: ChangeRequestRow[]; counts: Record<ChangeRequestStatus, number>; total: number };

type Clients = { state: "loading" } | { state: "failed"; message: string } | { state: "ready"; list: RequestClient[] };
type Inbox = { state: "loading" } | { state: "failed"; message: string } | { state: "ready"; rows: UnmatchedEmail[] };

/* ------------------------------------------------------------------------- *
 * Screen
 * ------------------------------------------------------------------------- */

type Open = { kind: "req"; row: ChangeRequestRow } | { kind: "add" } | null;

export function ClientRequests() {
    const snackbar = useSnackbar();
    const [, userInfo] = useUserProfileContext();
    const meId = userInfo?.id ? String(userInfo.id) : null;
    const [filter, setFilter] = useState<string>("all");
    const [client, setClient] = useState<string>("");
    const [open, setOpen] = useState<Open>(null);
    const [tick, setTick] = useState(0);
    const [clientsTick, setClientsTick] = useState(0);
    const [load, setLoad] = useState<Load>({ state: "loading", query: "" });
    const [clients, setClients] = useState<Clients>({ state: "loading" });
    const [inbox, setInbox] = useState<Inbox>({ state: "loading" });

    // The list, for the status and client filters. A reload keeps the rows on screen until the new ones arrive.
    useEffect(() => {
        let live = true;
        const query = `${filter}|${client}`;
        setLoad((prev) => (prev.state === "ready" && prev.query === query ? prev : { state: "loading", query }));
        void listChangeRequests({ status: filter === "all" ? undefined : (filter as ChangeRequestStatus), businessId: client || undefined, limit: 200 }).then((res) => {
            if (!live) return;
            setLoad(res.ok ? { state: "ready", query, rows: res.data.rows, counts: res.data.counts, total: res.data.pagination.total } : { state: "failed", query, message: res.message });
        });
        return () => {
            live = false;
        };
    }, [filter, client, tick]);

    useEffect(() => {
        let live = true;
        setClients((prev) => (prev.state === "ready" ? prev : { state: "loading" }));
        void listRequestClients().then((res) => {
            if (!live) return;
            setClients(res.ok ? { state: "ready", list: res.data } : { state: "failed", message: res.message });
        });
        return () => {
            live = false;
        };
    }, [clientsTick]);

    useEffect(() => {
        let live = true;
        setInbox((prev) => (prev.state === "ready" ? prev : { state: "loading" }));
        void listUnmatchedEmails().then((res) => {
            if (!live) return;
            setInbox(res.ok ? { state: "ready", rows: res.data } : { state: "failed", message: res.message });
        });
        return () => {
            live = false;
        };
    }, [tick]);

    const ready = load.state === "ready" ? load : null;
    const counts = ready?.counts;
    const late = ready ? ready.rows.filter(isLate).length : 0;
    const clientList = clients.state === "ready" ? clients.list : [];

    // One failed read, one line: when Oura can't be reached every read fails at once, and four
    // copies of the same error with four Try agains told nobody anything more (Jack, 22 Oct 2026).
    const failedRead = [load, clients, inbox].find((r) => r.state === "failed");
    const readFailure = failedRead?.state === "failed" ? failedRead : null;
    const retryAll = () => {
        setTick((t) => t + 1);
        setClientsTick((t) => t + 1);
    };

    const status =
        load.state === "failed"
            ? undefined
            : ready
              ? [`${ready.counts.open} need us`, `${ready.counts.in_progress} in progress`, late > 0 ? `${late} late` : null].filter(Boolean).join(", ")
              : "Loading";

    /** A request changed: show the server's row now and read the list again. */
    const saved = (next: ChangeRequestRow) => {
        setLoad((cur) => (cur.state === "ready" ? { ...cur, rows: cur.rows.map((r) => (r.id === next.id ? next : r)) } : cur));
        setOpen({ kind: "req", row: next });
        setTick((t) => t + 1);
    };

    const added = (created: number, merged: number) => {
        setFilter("all");
        setOpen(null);
        setTick((t) => t + 1);
        snackbar.showSuccess([created > 0 ? `Added ${plural(created, "request")}` : null, merged > 0 ? `${merged} asked again` : null].filter(Boolean).join(", ") + ".");
    };

    return (
        <FoldScope value="oura-requests">
            <PageHead
                title="Client requests"
                sentence="Every change a client asks us for, from any channel, in one list."
                status={status}
                actions={
                    <span>
                        <PillButton variant="primary" icon={ClipboardPasteIcon} writes onClick={() => setOpen({ kind: "add" })}>
                            Add a request
                        </PillButton>
                    </span>
                }
            />

            <div className="space-y-5">
                {readFailure && (
                    <Surface className="!py-1">
                        <ReadError message={readFailure.message} onRetry={retryAll} />
                    </Surface>
                )}
                <InboxCard inbox={inbox} clients={clientList} onFiled={() => setTick((t) => t + 1)} />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <StatusKey
                        groups={counts ? CHANGE_REQUEST_STATUSES.map((s) => ({ key: s, label: STATUS[s].label, tone: STATUS[s].tone, count: counts[s] })) : []}
                        value={filter}
                        onChange={setFilter}
                        total={counts ? undefined : null}
                        className="flex-1"
                    />
                    <select
                        aria-label="Client"
                        value={client}
                        onChange={(e) => setClient(e.target.value)}
                        className={cn(TYPE.tc, "h-[30px] rounded-full bg-white px-3 font-medium text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600")}
                    >
                        <option value="">All clients</option>
                        {clientList.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                {load.state !== "failed" && <Surface>
                    {load.state === "loading" ? (
                        <DrawerPulse lines={3} label="Loading requests" />
                    ) : ready && ready.rows.length === 0 ? (
                        <p className={cn(TYPE.tb, "m-0 py-6 text-center text-gray-500")}>Nothing here.</p>
                    ) : (
                        <div className="space-y-2">
                            {ready &&
                                CHANGE_REQUEST_STATUSES.filter((s) => ready.rows.some((r) => r.status === s)).map((s) => {
                                    const rows = ready.rows.filter((r) => r.status === s);
                                    return (
                                        <GroupFold
                                            key={s}
                                            id={`requests.${s}`}
                                            lead="square"
                                            tone={STATUS[s].tone}
                                            title={STATUS[s].label}
                                            count={rows.length}
                                            defaultFolded={s === "done"}
                                            remember={false}
                                        >
                                            <div>
                                                {rows.map((r) => (
                                                    <RequestRow key={r.id} r={r} onOpen={() => setOpen({ kind: "req", row: r })} />
                                                ))}
                                            </div>
                                        </GroupFold>
                                    );
                                })}
                        </div>
                    )}
                </Surface>}
                {ready && ready.total > ready.rows.length && (
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                        Showing the newest {ready.rows.length} of {ready.total}. Narrow it by client or status to see the rest.
                    </p>
                )}
            </div>

            <Drawer open={open !== null} onClose={() => setOpen(null)} itemKey={open?.kind === "req" ? open.row.id : (open?.kind ?? undefined)} width={620}>
                {open?.kind === "req" && <RequestSheet r={open.row} meId={meId} onSaved={saved} onClose={() => setOpen(null)} />}
                {open?.kind === "add" && (
                    <AddSheet
                        clients={clients}
                        rows={ready?.rows ?? []}
                        onRetryClients={() => setClientsTick((t) => t + 1)}
                        onAdded={added}
                        onCancel={() => setOpen(null)}
                    />
                )}
            </Drawer>
        </FoldScope>
    );
}

function ReadError({ message, onRetry }: { message: string; onRetry: () => void }) {
    return (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 py-3">
            <p className={cn(TYPE.tb, "m-0 text-rose-600")}>{message}</p>
            <PillButton size="sm" onClick={onRetry}>
                Try again
            </PillButton>
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * The oura@ inbox: emails no single client matched
 * ------------------------------------------------------------------------- */

function InboxCard({ inbox, clients, onFiled }: { inbox: Inbox; clients: RequestClient[]; onFiled: () => void }) {
    const waiting = inbox.state === "ready" ? inbox.rows : [];
    return (
        <Surface className="!py-3.5">
            <div className="flex items-center gap-3">
                <IconSquare tone="orange" icon={InboxIcon} />
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>oura@ourteam.ai</p>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Client emails to this address are read for you and added below. An email no client matched waits here until you pick one.</p>
                </div>
                {waiting.length > 0 && <Tag tone="blue">{plural(waiting.length, "email")} waiting</Tag>}
            </div>
            {inbox.state === "loading" && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>Checking the inbox</p>}
            {/* The page's one error line says why; this card only says it has nothing to show. */}
            {inbox.state === "failed" && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>Inbox not checked</p>}
            {waiting.map((e) => (
                <InboxRow key={e.id} email={e} clients={clients} onFiled={onFiled} />
            ))}
        </Surface>
    );
}

function InboxRow({ email, clients, onFiled }: { email: UnmatchedEmail; clients: RequestClient[]; onFiled: () => void }) {
    const [pick, setPick] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const file = async () => {
        setBusy(true);
        setError(null);
        const res = await assignUnmatchedEmail(email.id, pick);
        setBusy(false);
        if (!res.ok) {
            setError(res.message);
            return;
        }
        onFiled();
    };

    return (
        <div className="mt-3 rounded-xl bg-white p-3 ring-1 ring-black/[0.04]">
            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{email.subject || "No subject"}</p>
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{[email.from, email.receivedAt && dayText(email.receivedAt)].filter(Boolean).join(" · ")}</p>
            {email.state === "failed" && <p className={cn(TYPE.tc, "m-0 mt-1 text-rose-600")}>{email.error || "The read failed."}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                    aria-label="Client for this email"
                    value={pick}
                    onChange={(e) => setPick(e.target.value)}
                    className={cn(TYPE.tc, "h-7 rounded-full bg-white px-2.5 font-medium text-gray-900 ring-1 ring-inset ring-gray-200")}
                >
                    <option value="">Pick the client</option>
                    {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                <PillButton writes size="sm" variant="primary" disabled={!pick || busy} onClick={() => void file()}>
                    File it
                </PillButton>
            </div>
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-600")}>
                    {error}
                </p>
            )}
        </div>
    );
}

function RequestRow({ r, onOpen }: { r: ChangeRequestRow; onOpen: () => void }) {
    const src = sourceOf(r.source);
    const lateNow = isLate(r);
    return (
        <ListRow
            lead="square"
            icon={<IconSquare tone={src.tone} icon={src.icon} />}
            title={r.title || r.note}
            sub={[r.business.name, r.requestedBy, src.label].filter(Boolean).join(" · ")}
            tags={
                (r.askedAgain.length > 0 || (r.owner && r.status !== "done")) && (
                    <>
                        {r.askedAgain.length > 0 && <Tag tone="amber">Asked again</Tag>}
                        {r.owner && r.status !== "done" && <Owner name={r.owner.name} />}
                    </>
                )
            }
            late={lateNow && r.due ? `Was due ${dayText(r.due)}` : undefined}
            meta={r.status === "done" ? "Done" : r.due ? `Due ${dayText(r.due)}` : askedText(r.at)}
            muted={r.status === "done"}
            onClick={onOpen}
            stackMeta
        />
    );
}

function Owner({ name }: { name: string }) {
    return (
        <span title={`${name} is on it`} className="grid h-6 w-6 place-items-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-700">
            {name.charAt(0)}
        </span>
    );
}

/* ------------------------------------------------------------------------- *
 * One request
 * ------------------------------------------------------------------------- */

const FIELD = cn(TYPE.tb, "w-full rounded-lg bg-white px-3 py-2 text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600");

function RequestSheet({ r, meId, onSaved, onClose }: { r: ChangeRequestRow; meId: string | null; onSaved: (next: ChangeRequestRow) => void; onClose: () => void }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [draftTitle, setDraftTitle] = useState("");
    const [draftDue, setDraftDue] = useState("");
    const [reply, setReply] = useState("");
    /** Screenshots on the reply: uploaded only on Send reply. */
    const [pics, setPics] = useState<File[]>([]);
    const writesPaused = useWritesPaused();
    const drop = usePictureInput(pics, setPics, setError, REPLY_PICTURES_ON && !busy && !writesPaused);
    const src = sourceOf(r.source);
    const st = statusOf(r.status);
    const lateNow = isLate(r);
    const title = r.title || r.note;
    const last = r.askedAgain[r.askedAgain.length - 1];
    // The conversation after the ask, which the block under "What they sent" shows (#4930). The
    // thread is checked here: this page's rows come straight from the server, and a patch or reply merges into them.
    const later = everyMessage({ ...r, thread: requestMessages(r.thread) }).slice(1);
    // Taking a request also makes the signed-in staffer its owner, as it always has.
    const ownerPatch: ChangeRequestPatch = r.owner || !meId ? {} : { owner: { userId: meId } };

    const save = async (body: ChangeRequestPatch): Promise<boolean> => {
        setBusy(true);
        setError(null);
        const res = await patchChangeRequest(r.id, body);
        setBusy(false);
        if (!res.ok) {
            setError(res.message);
            return false;
        }
        onSaved({ ...r, ...res.data });
        return true;
    };

    const move = (to: ChangeRequestStatus) => void save({ status: to, ...ownerPatch });

    const startEdit = () => {
        setDraftTitle(title);
        setDraftDue(r.due ?? "");
        setEditing(true);
    };

    const saveDetails = async () => {
        if (await save({ title: draftTitle, due: draftDue || null })) setEditing(false);
    };

    const sendReply = async () => {
        setBusy(true);
        setError(null);
        try {
            const keys: string[] = [];
            for (const f of pics) {
                const up = await uploadChangeRequestImage(f);
                if (!up.ok) {
                    setError(`${f.name} did not upload. Your reply is still here. Try again.`);
                    return;
                }
                keys.push(up.data.fileKey);
            }
            const res = await replyToChangeRequest(r.id, reply.trim(), keys);
            if (!res.ok) {
                setError(res.message);
                return;
            }
            setReply("");
            setPics([]);
            onSaved({ ...r, ...res.data });
        } catch {
            setError("Your reply did not send. It is still here. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <DrawerHead
                lead={<src.icon aria-hidden size={14} className="text-blue-600" />}
                title={title}
                status={{
                    tone: st.tone,
                    label: st.label,
                    clock: r.due && r.status !== "done" ? { text: lateNow ? `Was due ${dayText(r.due)}` : `Due ${dayText(r.due)}`, late: lateNow } : null,
                }}
                chips={
                    <>
                        <Link to={ouraHref("reviews", { role: "oura-staff", businessId: r.businessId })} className={cn(TYPE.tc, "font-medium text-blue-700 hover:underline")}>
                            {r.business.name}
                        </Link>
                        <Tag tone="gray">{src.label}</Tag>
                    </>
                }
            />
            <div className="space-y-3">
                {error && (
                    <p role="alert" className={cn(TYPE.tb, "m-0 rounded-xl bg-rose-50 px-3 py-2 text-rose-700")}>
                        {error}
                    </p>
                )}
                {last && (
                    <DrawerNotice>
                        {r.requestedBy || "The client"} asked for this again on {dayText(last.at)}. First asked {dayText(r.at)}.
                    </DrawerNotice>
                )}
                <div>
                    <div className="mb-1.5 flex items-center justify-between px-1">
                        <p className={cn(TYPE.lab, "m-0")}>Details</p>
                        {!editing && <EditButton onClick={startEdit} label="Edit" />}
                    </div>
                    {editing ? (
                        <div className="space-y-3 rounded-2xl bg-white p-4 ring-1 ring-black/[0.04]">
                            <label className="block space-y-1">
                                <span className={cn(TYPE.tm, "text-gray-500")}>Request</span>
                                <input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} aria-label="Request" className={FIELD} />
                            </label>
                            <label className="block space-y-1">
                                <span className={cn(TYPE.tm, "text-gray-500")}>Due</span>
                                <input type="date" value={draftDue} onChange={(e) => setDraftDue(e.target.value)} aria-label="Due" className={FIELD} />
                            </label>
                            <div className="flex gap-2">
                                <PillButton writes size="sm" variant="primary" disabled={busy} onClick={() => void saveDetails()}>
                                    Save
                                </PillButton>
                                <PillButton size="sm" onClick={() => setEditing(false)}>
                                    Cancel
                                </PillButton>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-2">
                            <Fact icon={GlobeIcon} label="Where" value={r.where || "Not given"} />
                            <Fact icon={CalendarDaysIcon} label="Due" value={r.due ? dayText(r.due) : "No date"} late={lateNow} />
                            <Fact icon={UserRoundIcon} label="Who’s on it" value={r.owner?.name || "Nobody yet"} />
                        </div>
                    )}
                </div>
                <div>
                    <p className={cn(TYPE.lab, "m-0 mb-1.5 px-1")}>What they sent</p>
                    <ClientMessage
                        who={r.requestedBy?.trim() || r.business.name}
                        via={src.label}
                        when={dayText(r.at)}
                        subject={r.original?.email?.subject || undefined}
                        text={r.original?.text || r.note || (r.files.length > 0 ? "" : "No message.")}
                        files={r.files.length > 0 ? r.files.map(storedAttachment) : undefined}
                    />
                </div>
                {/* The conversation after the ask, oldest first: our replies and their follow-ups (#4930, #6874). */}
                {later.length > 0 && (
                    <div>
                        <p className={cn(TYPE.lab, "m-0 mb-1.5 px-1")}>{later.some((m) => m.from === "client") ? "Conversation" : later.length > 1 ? "Our replies" : "Our reply"}</p>
                        <div className="space-y-2">
                            {later.map((m, i) =>
                                m.from === "client" ? (
                                    <ClientMessage
                                        key={`${m.at}:${i}`}
                                        who={m.by?.trim() || r.requestedBy?.trim() || r.business.name}
                                        via={SOURCE.oura.label}
                                        when={m.at ? `followed up ${dayText(m.at)}` : "followed up"}
                                        text={m.body}
                                        files={m.files.length > 0 ? m.files.map(storedAttachment) : undefined}
                                    />
                                ) : (
                                    <div key={`${m.at}:${i}`} className="rounded-2xl bg-white p-4 ring-1 ring-black/[0.04]">
                                        <p className={cn(TYPE.tb, "m-0 whitespace-pre-wrap text-gray-900")}>{m.body}</p>
                                        <ReplyPictureGrid files={m.files} />
                                        <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>
                                            {[m.by, m.at ? dayText(m.at) : null].filter(Boolean).join(" · ")}
                                        </p>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                )}
                <div>
                    <p className={cn(TYPE.lab, "m-0 mb-1.5 px-1")}>Reply</p>
                    <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        rows={3}
                        aria-label="Reply"
                        placeholder="Write the reply"
                        {...drop.handlers}
                        className={cn(FIELD, "resize-y", drop.over && "bg-blue-50 ring-2 ring-blue-600")}
                    />
                    {REPLY_PICTURES_ON && <ReplyPicturePicker files={pics} onFiles={setPics} onError={setError} disabled={busy || writesPaused} />}
                    <div className="mt-2 flex justify-end">
                        <PillButton writes size="sm" variant="primary" disabled={busy || !reply.trim()} onClick={() => void sendReply()}>
                            Send reply
                        </PillButton>
                    </div>
                </div>
            </div>
            <DrawerFoot
                first={
                    r.status === "done" ? (
                        <PillButton onClick={onClose}>Close</PillButton>
                    ) : r.status === "open" && !r.owner ? (
                        <PillButton writes variant="primary" disabled={busy} onClick={() => move("in_progress")}>
                            I’ll take it
                        </PillButton>
                    ) : (
                        <PillButton writes variant="primary" icon={CheckIcon} disabled={busy} onClick={() => move("done")}>
                            Mark done
                        </PillButton>
                    )
                }
                second={
                    r.status === "done" ? undefined : r.status === "waiting_on_client" ? (
                        <PillButton writes disabled={busy} onClick={() => move("in_progress")}>
                            Client replied
                        </PillButton>
                    ) : (
                        <PillButton writes disabled={busy} onClick={() => move("waiting_on_client")}>
                            Waiting on client
                        </PillButton>
                    )
                }
            />
        </>
    );
}

function Fact({ icon: Icon, label, value, late }: { icon: LucideIcon; label: string; value: string; late?: boolean }) {
    return (
        <div className="min-w-0 rounded-xl bg-white px-3 py-2.5 ring-1 ring-black/[0.04]">
            <p className={cn(TYPE.tm, "m-0 flex items-center gap-1.5 text-gray-500")}>
                <Icon aria-hidden size={12} />
                {label}
            </p>
            <p className={cn(TYPE.tb, "m-0 mt-0.5 truncate font-medium", late ? "text-rose-600" : "text-gray-900")}>{value}</p>
        </div>
    );
}

/* ------------------------------------------------------------------------- *
 * Add a request: paste, Oura reads it, staff confirm
 * ------------------------------------------------------------------------- */

/** One request the reader found, as staff are checking it. */
interface AddDraft {
    key: string;
    keep: boolean;
    title: string;
    where: string;
    due: string | null;
    clientId: string;
    ask: string;
    who: string | null;
    repeatOf: string | null;
}

/** An estimate the chooser can trust: every option says whether it fits the budget. One that does not is not a
 *  reason to allow it (Jack, #4812); the read then goes ahead on Gemini, as when the estimate fails. */
function usableEstimate(e: ReadEstimate): boolean {
    return e.choose && e.options.length > 0 && e.options.every((o) => typeof o.withinBudget === "boolean");
}

/** One model's line under its name: what this message would cost, or why it cannot be picked. */
function engineLine(o: ReadEngineOption, e: ReadEstimate): string {
    if (!o.available) return o.reason ?? "Not available on this server.";
    if (!o.withinBudget) {
        return e.budget.open
            ? `Could cost up to ${costText(o.maxUsd) ?? "more"}, more than is left of this month’s reading budget.`
            : "This month’s reading budget is used up.";
    }
    const about = costText(o.estimateUsd);
    const most = costText(o.maxUsd);
    const cost = about ? `About ${about}${most && most !== about ? `, at most ${most}` : ""}` : "Cost not known";
    return `${cost} · ${o.model}${o.inputCounted ? "" : " · estimated from the length"}`;
}

/** This month's reading so far, in the measure the cap uses. */
function budgetLine(e: ReadEstimate): string {
    const b = e.budget;
    if (b.priced) return `This month: ${costText(b.spentUsd ?? 0) ?? "$0.00"} of $${b.capUsd.toFixed(2)} used.`;
    return `This month: ${b.reads} of ${b.readsCap} reads.`;
}

function AddSheet({
    clients,
    rows,
    onRetryClients,
    onAdded,
    onCancel,
}: {
    clients: Clients;
    rows: ChangeRequestRow[];
    onRetryClients: () => void;
    onAdded: (created: number, merged: number) => void;
    onCancel: () => void;
}) {
    const [step, setStep] = useState<"paste" | "reading" | "choose" | "found">("paste");
    /** The priced choice of model, when the server offers more than one (#6685), and what it will read. */
    const [estimate, setEstimate] = useState<ReadEstimate | null>(null);
    const [engine, setEngine] = useState<ReadEngine | null>(null);
    const [pending, setPending] = useState<{ body: string; fileKeys: string[] } | null>(null);
    const [text, setText] = useState("");
    const [files, setFiles] = useState<Attachment[]>([]);
    const [sent, setSent] = useState("");
    const [drafts, setDrafts] = useState<AddDraft[]>([]);
    const [readError, setReadError] = useState<string | null>(null);
    const [addError, setAddError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const clientList = clients.state === "ready" ? clients.list : [];

    const read = async () => {
        setReadError(null);
        setStep("reading");
        // Screenshots are stored first: the reader and the requests both take their keys.
        const next = [...files];
        for (let i = 0; i < next.length; i++) {
            const f = next[i];
            if (f.kind !== "image" || f.fileKey || !f.file) continue;
            const up = await uploadChangeRequestImage(f.file);
            if (!up.ok) {
                setFiles(next);
                setReadError(up.message);
                setStep("paste");
                return;
            }
            next[i] = { ...f, fileKey: up.data.fileKey };
        }
        setFiles(next);
        // PDFs and text files are read here for their words; they are not stored.
        const inFiles = await textOf(next);
        if (inFiles.unreadable.length > 0) {
            setReadError(`Oura couldn’t read ${inFiles.unreadable.join(", ")}. Remove or replace it and try again.`);
            setStep("paste");
            return;
        }
        const body = [text, inFiles.text].filter((t) => t.trim()).join("\n\n");
        const fileKeys = next.flatMap((f) => (f.fileKey ? [f.fileKey] : []));
        // With more than one model on offer, staff see what each would cost and pick (#6685). Nothing
        // here ever blocks the read:
        //   * no estimate route (a server from before #6685) or nothing to choose: the read as it always
        //     was, with no engine (Jack, #4813);
        //   * an estimate that failed or came back incomplete on a server that does choose: Gemini, the
        //     cheap one, never whatever the server is configured with (Claude, when there is a choice).
        const est = await estimateChangeRequestRead({ text: body, fileKeys });
        if (est.ok) {
            const e = est.data;
            if (e === null || !e.choose) {
                await readWith(body, fileKeys);
                return;
            }
            if (usableEstimate(e)) {
                const usable = e.options.filter((o) => o.available && o.withinBudget);
                setEstimate(e);
                setEngine(usable.some((o) => o.engine === e.default) ? e.default : (usable[0]?.engine ?? null));
                setPending({ body, fileKeys });
                setStep("choose");
                return;
            }
        }
        await readWith(body, fileKeys, "gemini");
    };

    const readWith = async (body: string, fileKeys: string[], chosen?: ReadEngine) => {
        setReadError(null);
        setStep("reading");
        const res = await readChangeRequests({ text: body, fileKeys, ...(chosen ? { engine: chosen } : {}) });
        if (!res.ok) {
            setReadError(res.message);
            setStep("paste");
            return;
        }
        setSent(body);
        setDrafts(
            res.data.drafts.map((d, i) => ({
                key: `d${i}`,
                keep: true,
                title: d.title,
                where: d.where,
                due: d.due,
                clientId: d.businessId ?? "",
                ask: d.ask,
                who: d.who,
                repeatOf: d.repeatOf,
            })),
        );
        setStep("found");
    };

    const kept = drafts.filter((d) => d.keep);
    const canAdd = kept.length > 0 && kept.every((d) => d.clientId && d.title.trim());
    const patch = (key: string, p: Partial<AddDraft>) => setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...p } : d)));
    const storedKeys = files.flatMap((f) => (f.fileKey ? [f.fileKey] : []));

    const add = async () => {
        setAdding(true);
        setAddError(null);
        const items: ChangeRequestItem[] = kept.map((d) => ({
            title: d.title.trim(),
            ask: d.ask || d.title.trim(),
            who: d.who || undefined,
            where: d.where || undefined,
            due: d.due,
            businessId: d.clientId,
            repeatOf: d.repeatOf,
        }));
        const res = await addChangeRequests({ source: storedKeys.length > 0 ? "screenshot" : "text", original: { text: sent, fileKeys: storedKeys, email: null }, items });
        setAdding(false);
        if (!res.ok) {
            setAddError(res.message);
            return;
        }
        onAdded(res.data.created.length, res.data.merged.length);
    };

    if (step === "paste") {
        return (
            <>
                <DrawerHead lead={<ClipboardPasteIcon aria-hidden size={14} className="text-blue-600" />} title="Add a request" />
                <div className="space-y-3">
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>
                        Paste what the client sent, or add their screenshots, photos and PDFs. Oura turns it into requests with a brief for each.
                    </p>
                    <PasteBox text={text} onText={setText} files={files} onFiles={(f) => { setFiles(f); setReadError(null); }} />
                    {readError && (
                        <p role="alert" className={cn(TYPE.tm, "m-0 text-rose-600")}>
                            {readError}
                        </p>
                    )}
                </div>
                <DrawerFoot
                    first={
                        <PillButton writes variant="primary" icon={SparklesIcon} disabled={!text.trim() && files.length === 0} onClick={() => void read()}>
                            Read it
                        </PillButton>
                    }
                    second={<PillButton onClick={onCancel}>Cancel</PillButton>}
                />
            </>
        );
    }

    if (step === "choose" && estimate && pending) {
        const picked = estimate.options.find((o) => o.engine === engine);
        return (
            <>
                <DrawerHead lead={<SparklesIcon aria-hidden size={14} className="text-blue-600" />} title="Choose who reads it" />
                <DrawerBlock label={<span id="read-engine-label">Model</span>}>
                    <RadioList
                        labelId="read-engine-label"
                        value={engine}
                        onPick={(k) => setEngine(k as ReadEngine)}
                        options={estimate.options.map((o) => ({
                            key: o.engine,
                            label: o.label,
                            disabled: !o.available || !o.withinBudget,
                            sub: engineLine(o, estimate),
                        }))}
                    />
                    <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>
                        Counted from this message and its files; the answer is this model’s usual length here. A reply that has to be
                        asked again can cost up to the higher figure. {budgetLine(estimate)}
                    </p>
                </DrawerBlock>
                <DrawerFoot
                    first={
                        <PillButton writes variant="primary" icon={SparklesIcon} disabled={!picked || !picked.available || !picked.withinBudget}
                            onClick={() => void readWith(pending.body, pending.fileKeys, engine ?? undefined)}>
                            Read it
                        </PillButton>
                    }
                    second={<PillButton onClick={() => setStep("paste")}>Back</PillButton>}
                />
            </>
        );
    }

    if (step === "reading") {
        return (
            <>
                <DrawerHead lead={<SparklesIcon aria-hidden size={14} className="text-blue-600" />} title="Reading it" />
                <DrawerBlock label={files.length > 0 ? "Oura is reading the message and the files" : "Oura is picking out each request"} busy>
                    <DrawerPulse lines={3} label="Reading" />
                </DrawerBlock>
            </>
        );
    }

    const source: ChangeRequestSource = storedKeys.length > 0 ? "screenshot" : "text";
    const who = drafts.find((d) => d.who)?.who ?? "";
    return (
        <>
            <DrawerHead
                lead={<SparklesIcon aria-hidden size={14} className="text-blue-600" />}
                title={drafts.length === 0 ? "Oura found no requests" : drafts.length === 1 ? "Oura found 1 request" : `Oura found ${drafts.length} requests`}
            />
            <div className="space-y-3">
                {clients.state === "failed" && <ReadError message={clients.message} onRetry={onRetryClients} />}
                {drafts.length === 0 ? (
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Nothing in what was pasted reads as a request. Go back and check it.</p>
                ) : (
                    <p className={cn(TYPE.tb, "m-0 text-gray-600")}>Check each one, fix the wording if needed, then add. Untick anything that isn’t a request.</p>
                )}
                {drafts.map((d, i) => (
                    <DraftCard
                        key={d.key}
                        d={d}
                        n={drafts.length > 1 ? i + 1 : null}
                        clients={clientList}
                        before={d.repeatOf ? rows.find((r) => r.id === d.repeatOf) : undefined}
                        askedBefore={Boolean(d.repeatOf)}
                        onChange={(p) => patch(d.key, p)}
                    />
                ))}
                {drafts.length > 0 && (
                    <div>
                        <p className={cn(TYPE.lab, "m-0 mb-1.5 px-1")}>What they sent, saved with each request</p>
                        <ClientMessage who={who} via={sourceOf(source).label} when="Today" text={sent} files={files.filter((f) => f.fileKey)} />
                    </div>
                )}
                {addError && (
                    <p role="alert" className={cn(TYPE.tm, "m-0 text-rose-600")}>
                        {addError}
                    </p>
                )}
            </div>
            <DrawerFoot
                first={
                    <PillButton writes variant="primary" disabled={!canAdd || adding} onClick={() => void add()}>
                        {addLabel(kept)}
                    </PillButton>
                }
                second={<PillButton onClick={() => setStep("paste")}>Back</PillButton>}
            />
        </>
    );
}

function addLabel(kept: AddDraft[]): string {
    const fresh = kept.filter((d) => !d.repeatOf).length;
    const again = kept.length - fresh;
    if (again === 0) return fresh === 1 ? "Add 1 request" : `Add ${fresh} requests`;
    if (fresh === 0) return again === 1 ? "Update 1 request" : `Update ${again} requests`;
    return `Add ${fresh}, update ${again}`;
}

function DraftCard({
    d,
    n,
    clients,
    before,
    askedBefore,
    onChange,
}: {
    d: AddDraft;
    n: number | null;
    clients: RequestClient[];
    before: ChangeRequestRow | undefined;
    askedBefore: boolean;
    onChange: (p: Partial<AddDraft>) => void;
}) {
    return (
        <div className={cn("rounded-2xl bg-white ring-1 ring-black/[0.04] transition-opacity", !d.keep && "opacity-50")}>
            <div className="flex items-start gap-3 p-4 pb-3">
                <input
                    type="checkbox"
                    checked={d.keep}
                    onChange={(e) => onChange({ keep: e.target.checked })}
                    aria-label="Keep this request"
                    className="mt-2.5 h-4 w-4 shrink-0 accent-blue-600"
                />
                <div className="min-w-0 flex-1 space-y-2">
                    <p className={cn(TYPE.tm, "-mt-1 m-0 font-semibold text-gray-400")}>{n !== null ? `Request ${n}` : "Request"}</p>
                    <textarea
                        value={d.title}
                        rows={d.title.length > 60 ? 2 : 1}
                        aria-label="Request"
                        onChange={(e) => onChange({ title: e.target.value })}
                        className={cn(
                            TYPE.tb,
                            "-mx-2 block w-[calc(100%+16px)] resize-none rounded-lg border-0 bg-transparent px-2 py-1 text-[15px] font-semibold leading-[22px] text-gray-900 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-600",
                        )}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            aria-label="Client"
                            value={d.clientId}
                            onChange={(e) => {
                                const clientId = e.target.value;
                                // A repeat belongs to one client: a new client drops it, so the batch adds a fresh request.
                                onChange({ clientId, repeatOf: before?.businessId === clientId ? d.repeatOf : null });
                            }}
                            className={cn(TYPE.tc, "h-7 rounded-full bg-white px-2.5 font-medium ring-1 ring-inset", d.clientId ? "text-gray-900 ring-gray-200" : "text-rose-600 ring-rose-200")}
                        >
                            <option value="">Pick the client</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                        {d.where && <Tag tone="gray">{d.where}</Tag>}
                        {d.due && <Tag tone="blue">Due {dayText(d.due)}</Tag>}
                    </div>
                </div>
            </div>
            {askedBefore && (
                <div className="px-4 pb-4 pl-11">
                    <Hint>
                        {before
                            ? `Asked before: “${before.title || before.note}” on ${dayText(before.at)}. This goes onto that one instead of a new row.`
                            : "Asked before. This goes onto that one instead of a new row."}
                    </Hint>
                </div>
            )}
        </div>
    );
}

function Hint({ children }: { children: ReactNode }) {
    return <p className={cn(TYPE.tc, "m-0 rounded-lg bg-amber-50 px-3 py-2 text-amber-800")}>{children}</p>;
}
